import { MutationCache, QueryClient } from '@tanstack/react-query'
import { ApiError } from './api'
import { useLive } from '@/stores/live'

/**
 * Query keys (03 §4.1): arrays, most general first, so invalidating a prefix
 * covers its children. One factory so the event→cache map and the screens can
 * never spell a key differently.
 */
export const qk = {
  me: () => ['me'] as const,
  overview: () => ['overview'] as const,
  projects: () => ['projects'] as const,
  project: (p: string) => ['project', p] as const,
  board: (p: string) => ['project', p, 'board'] as const,
  issues: (p: string) => ['project', p, 'issues'] as const,
  secrets: (p: string) => ['project', p, 'secrets'] as const,
  mcp: (p: string) => ['project', p, 'mcp'] as const,
  /** Workspace by ticket: the URL names the ticket, the API names the id. */
  workspace: (p: string, t: string) => ['workspace', p, t] as const,
  timeline: (id: string) => ['ws', id, 'timeline'] as const,
  diff: (id: string) => ['ws', id, 'diff'] as const,
  checks: (id: string) => ['ws', id, 'checks'] as const,
  sessions: (id: string) => ['ws', id, 'sessions'] as const,
  projectSessions: (project: string) => ['project', project, 'sessions'] as const,
  projectGit: (project: string) => ['project', project, 'git'] as const,
  projectReleases: (project: string) => ['project', project, 'releases'] as const,
  projectStages: (project: string) => ['project', project, 'stages'] as const,
  /** Stored chat messages, newest first; the socket adds and patches them. */
  messages: (id: string) => ['ws', id, 'messages'] as const,
  /** Live terminal output, appended by the socket and never fetched. */
  output: (id: string) => ['ws', id, 'output'] as const,
  wsAll: (id: string) => ['ws', id] as const,
  /** CI (features/21): runs, one run, one job's log; all under the project's CI prefix. */
  ci: (p: string) => ['project', p, 'ci'] as const,
  ciRuns: (p: string) => ['project', p, 'ci', 'runs'] as const,
  ciRun: (p: string, run: string) => ['project', p, 'ci', 'run', run] as const,
  ciLog: (p: string, job: string) => ['project', p, 'ci', 'log', job] as const,
  home: () => ['home'] as const,
  servers: () => ['servers'] as const,
  containers: () => ['containers'] as const,
  usage: (group: string, from: string, to: string, project?: string) => ['usage', group, from, to, project ?? 'all'] as const,
  usageSummary: (days: number, project?: string) => ['usage', 'summary', days, project ?? 'all'] as const,
  alerts: () => ['alerts'] as const,
  people: () => ['people'] as const,
  invites: () => ['invites'] as const,
  account: () => ['account'] as const,
  accountSessions: () => ['account', 'sessions'] as const,
  integrations: () => ['integrations'] as const,
  claudeAccounts: () => ['claude-accounts'] as const,
  backups: () => ['backups'] as const,
  claudeLogin: (id: string) => ['claude-accounts', 'login', id] as const,
}

export function makeQueryClient(): QueryClient {
  const qc: QueryClient = new QueryClient({
    // A mutation refused because somebody else got there first (409/412)
    // refetches everything it could have touched, so the screen shows what is
    // true now instead of what the person was looking at.
    mutationCache: new MutationCache({
      onError: (err) => {
        if (err instanceof ApiError && err.stale) {
          void qc.invalidateQueries()
          useLive.getState().pushToast({ level: 'info', text: err.what ?? err.message })
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // 4xx is an answer, not a blip: retrying a 403 only delays the message.
        retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      },
      mutations: { retry: false },
    },
  })
  return qc
}
